import { windowatch } from '../../../scripts/lib/windowatch';
import * as Sentry from '@sentry/browser';

import Dropzone from 'dropzone';

const name = 'form-file';

export default class FormFile {

  constructor ($el) {

    this.$el = $el;
    this.id = $el.dataset.id;
    this.type = $el.dataset.type;
    this.required = $el.classList.contains(`${name}--required`);
    this.validation = $el.dataset.validation || null;
    this.valid = true;
    this.delegate = null;
    this.progressCircleLength = 150.8;
    this.isMobile = windowatch.getBreakpoint() === 'xs';
    this.timeouts = [];
    this.threeHourTimeout = 10800000; // 3 hours in milliseconds
    this.tenMinutesTimeout = 600000, // 10 Minutes in milliseconds
    this.unfinishedProgress = 73; // Number to show when unfinished. The number is a personal preference

    this.files = [];
    this.header = null; // Used during file uploads.
    this.messageInvalid = $el.dataset.messageInvalid;
    this.messageRequired = $el.dataset.messageRequired;
    this.dropzone = null;

    let elementIdSelector = '[data-id=' + this.id + ']';
    let togglesClasses = elementIdSelector + ' .dz-file__dropzone-toggle';
    togglesClasses += ',' + elementIdSelector + ' .dz-file__dropzone-retry';
    this.dropzoneToggles = togglesClasses;

    // Bind elements
    this.$requiredInfo = $el.querySelector(`.${name}__required-info`);
    this.$input = $el.querySelector(`.${name}__dropzone`);
    this.$template = $el.querySelector(`.${name}__dropzone-template`);

    // Get field name
    this.name = this.$input.dataset.name;

    // Init Dropzone
    Dropzone.autoDiscover = false;
    this.init();
  }

  init () {
    this.initDropzone();
  }

  initDropzone () {
    let that = this;

    this.dropzone = new Dropzone(that.$input, {
      url: document.location.href, // We don't need any URL, this is a placeholder
      method: 'post',
      paramName: 'file',
      maxFilesize: 300,
      timeout: this.tenMinutesTimeout,
      acceptedFiles: '.jpg,.jpeg,.gif,.png,.tiff,.avi,.flv,.m4a,.mov,.mp3,.mp4,.mpg,.mpeg,.wma,.wmv',
      parallelUploads: 1,
      thumbnailWidth: 400,
      thumbnailHeight: 400,
      previewTemplate: this.$template.innerHTML,
      clickable: this.dropzoneToggles,
      dictCancelUploadConfirmation: "Bist du dir sicher, dass du den Upload abbrechen möchtest?",
      sending: (file, xhr, formData) => {

        // Override Dropzone's default sending behavior
        this.uploadToAzure(file);
      },
      uploadprogress: (file, progress, bytesSent) => {

        if (file.previewElement) {
          that.updateProgress(file, progress, false);
        }
      },
      success: (file, response) => {

        // Save file to files array
        that.files.push(file.upload.filename);
        that.validate();

        if (file.previewElement) {
          // Set a correct thumbnail and register deletion event
          that.setThumbnailIcon(file);

          file.previewElement.addEventListener(
            'click',
            that.onDeleteToggleClick.bind(
              that,
              file.previewElement.querySelector('.dz-file__delete-overlay')
            )
          );
        }

        // Expire file uploads after 3 hours
        this.timeouts[file.name] = window.setTimeout(() => {
          this.processthreeHourTimeout(file);
        }, this.threeHourTimeout);
      },
      error: (file, errorMsg, request) => {

        that.setErrorForFile(file);
      },
      removedfile: (file) => {
        that.processRemovedFile(file);
      }
    });
  }

  async uploadToAzure(file) {
    const fileName = file.name;

    try {
      // Send the file data to the Uploader for processing, see AzureUploadsApi.
      const response = await fetch(this.$input.dataset.uploadEndpoint, {
        method: 'POST',
        headers: {
          'X-Filename': fileName,
          'X-Submission-Id': this.header.submission_id,
        },
        body: file
      });

      const responseData = await response.json();

      // Check for errors
      if (!response.ok || responseData.success === false) {
        console.log(responseData.data.message);
        throw new Error('Failed to upload file');
      }
    } catch (error) {
      this.setErrorForFile(file);
      console.log(error);
    }
  }

  updateProgress (file, progress, finished) {
    if (!finished && progress > this.unfinishedProgress) {
      progress = this.unfinishedProgress;
    }

    let node = file.previewElement
      .querySelector('[data-dz-uploadprogress]');

    node.classList.remove('dz-file--upload-pending');

    node.querySelector('[data-dz-uploadprogress-value]')
      .innerText = `${progress.toFixed(0)}%`;

    let newDashOffset = (this.progressCircleLength / 100 * progress);
    newDashOffset = this.progressCircleLength - newDashOffset;
    node.querySelector('.dz-file__progressbar-circle')
      .setAttribute('stroke-dashoffset', newDashOffset.toFixed(2));

    node.setAttribute('data-progress', progress);
  }

  /**
   * Set the correct thumbnail for a file after the upload is finished
   *
   * @param file
   * @param response
   */
  setThumbnailIcon (file) {
    file.previewElement.classList.add('dz-success');

    // Show the correct file type icon
    let type = file.type.substr(0, file.type.lastIndexOf('/'));
    let previewIcon = file.previewElement
      .querySelector('.dz-file__preview-icon--' + type);
    if (previewIcon) {
      previewIcon.classList.add('dz-file__preview-icon--visible');
    }
  }

  /**
   * Update a file to be displayed with an error
   *
   * @param file
   */
  setErrorForFile (file) {
    // Add a class to the preview and text below it
    file.previewElement.classList.add('dz-file--error');
    file.previewElement.querySelector('[data-dz-name]').innerText
      = 'Oops! Hier ist etwas schiefgelaufen. Bitte versuche es erneut.';

    // Hide the progress icon
    file.previewElement.querySelector('[data-dz-uploadprogress]')
      .classList.add('dz-file__progress--hidden');

    // Make the preview a toggle with the retry-icon
    file.previewElement.classList.add('form-file__dropzone-retry');
    file.previewElement.querySelector('.dz-file__retry')
      .classList.add('dz-file__retry--visible');
  }

  /**
   * Remove a file from the upload server if it's removed from the files
   *
   * @param file
   */
  async processRemovedFile(file) {
    const fileName = file.name;

    try {
      // Send the file data to the Uploader for processing, see AzureUploadsApi.
      const response = await fetch(this.$input.dataset.removeEndpoint, {
        method: 'DELETE',
        headers: {
          'X-Filename': fileName,
          'X-Submission-Id': this.header.submission_id,
        },
      });

      const responseData = await response.json();

      // Check for errors
      if (!response.ok || responseData.success === false) {
        console.log(responseData.data.message);
        throw new Error('Failed to remove file');
      }

      // Remove file from files array and run validation
      this.files = this.files.filter(item => item !== file.name);
      this.validate();

      // Remove the file timeout and reference
      clearTimeout(this.timeouts[fileName]);
      delete this.timeouts[fileName];

      // Remove preview
      if (file.previewElement != null && file.previewElement.parentNode != null) {
        file.previewElement.parentNode.removeChild(file.previewElement);
      }
    } catch (error) {
      this.setErrorForFile(file);
      console.log(error);
    }
  }

  processthreeHourTimeout (file) {
    alert(this.$el.dataset.messagethreeHourTimeout);

    // Remove the file from Blob and Dropzone on timeout.
    this.processRemovedFile(file);
    this.dropzone.removeFile(file);
    this.files = this.files.filter(item => item !== file.name);

    this.validate();
  }

  onDeleteToggleClick (element) {
    element.querySelector('.dz-file__delete-toggle')
      .classList.add('dz-file__delete-toggle--visible');
  }

  /**
   * Sets this field's delegate.
   * @param {object} delegate
   * @return this
   */
  setDelegate (delegate) {
    this.delegate = delegate;

    return this;
  }

  /**
   * Returns field name.
   * This method gets called from the parent form instance.
   * @return {string} Field name
   */
  getName () {
    return this.name;
  }

  /**
   * Returns field value.
   * This method gets called from the parent form instance.
   * @return {string} Field value
   */
  getValue () {
    return '#SUBMISSION_ID#';
  }

  /**
   * Returns wether this field is valid.
   * This method gets called from the parent form instance.
   * @return {boolean}
   */
  isValid () {
    return this.valid;
  }

  /**
   * Validates field.
   * This method gets called from the parent form instance.
   * @return {boolean}
   */
  validate () {
    const valid = !this.required || (this.required && this.files.length > 0);

    this.setValid(valid);

    return this.isValid();
  }

  /**
   * Sets wether this field is valid.
   * @param {boolean} valid
   */
  setValid (valid) {
    if (this.valid !== valid) {
      this.valid = valid;

      // Update invalid modifier and required text
      this.$el.classList.toggle(`${name}--invalid`, !valid);
      this.$requiredInfo.classList.toggle(`${name}__message--hidden`);

      // Inform delegate about this
      this.delegate && this.delegate.fieldValidDidChange(this, valid);
    }

    return this;
  }

  setHeaderValues (header) {
    this.header = header;
  }
}
